import {memo, useState} from 'react'
import {ActivityIndicator, View} from 'react-native'
import {Trans, useLingui} from '@lingui/react/macro'

import {useCallOnce} from '#/lib/once'
import {useSearchClassroomsQuery} from '#/state/queries/classrooms/search'
import {useSession} from '#/state/session'
import {List} from '#/view/com/util/List'
import {ClassroomCard} from '#/screens/Classroom/components/ClassroomCard'
import {atoms as a, useTheme} from '#/alf'
import * as Layout from '#/components/Layout'
import {ListFooter} from '#/components/Lists'
import {Text} from '#/components/Typography'
import {useAnalytics} from '#/analytics'

function NoticeText({children}: {children: React.ReactNode}) {
  const t = useTheme()
  return (
    <Layout.Content>
      <View style={[a.p_xl]}>
        <View style={[t.atoms.bg_contrast_25, a.rounded_sm, a.p_lg]}>
          <Text style={[a.text_md]}>{children}</Text>
        </View>
      </View>
    </Layout.Content>
  )
}

let ClassroomSearchResults = ({
  query,
  active,
}: {
  query: string
  active: boolean
}): React.ReactNode => {
  const ax = useAnalytics()
  const {t: l} = useLingui()
  const {hasSession} = useSession()
  const [isPTR, setIsPTR] = useState(false)

  const {
    isFetched,
    data,
    isFetching,
    error,
    refetch,
    fetchNextPage,
    isFetchingNextPage,
    hasNextPage,
  } = useSearchClassroomsQuery(query, active)

  const classrooms = data?.pages.flatMap(page => page.classrooms) ?? []

  const fireTracking = useCallOnce(() => {
    ax.metric('search:results:loaded', {
      tab: 'classrooms',
      initialCount: classrooms.length,
    })
  })
  if (isFetched) {
    fireTracking()
  }

  const onPullToRefresh = async () => {
    setIsPTR(true)
    await refetch()
    setIsPTR(false)
  }
  const onEndReached = () => {
    if (isFetching || !hasNextPage || error) return
    void fetchNextPage()
  }

  if (error) {
    return (
      <NoticeText>
        {l`We’re sorry, but your search could not be completed. Please try again in a few minutes.`}
      </NoticeText>
    )
  }

  if (!isFetched) {
    return (
      <Layout.Content>
        <View style={[a.py_xl]}>
          <ActivityIndicator />
        </View>
      </Layout.Content>
    )
  }

  if (!classrooms.length) {
    return (
      <NoticeText>
        <Trans>No classrooms match “{query}”</Trans>
      </NoticeText>
    )
  }

  return (
    <List
      data={classrooms}
      renderItem={({item}) => (
        <View style={[a.px_lg, a.pt_md]}>
          <ClassroomCard classroom={item} />
        </View>
      )}
      keyExtractor={item => item.id}
      refreshing={isPTR}
      onRefresh={() => void onPullToRefresh()}
      onEndReached={onEndReached}
      desktopFixedHeight
      ListFooterComponent={
        <ListFooter
          hasNextPage={hasNextPage && hasSession}
          isFetchingNextPage={isFetchingNextPage}
        />
      }
    />
  )
}
ClassroomSearchResults = memo(ClassroomSearchResults)
export {ClassroomSearchResults}
